import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { skipToken } from "@reduxjs/toolkit/query";
import type Individual from "marketplace/individuals/individual";
import { useEffect, useMemo, useState } from "react";
import UserCoworkerQuery, {
  USER_COWORKER_QUERY_MAXIMUM_LENGTH,
  UserCoworkerFilterParameters,
  UserCoworkerOrderField,
  UserCoworkerOrderParameters,
} from "users/information/coworker-information/_api/commands/query-user-coworker-api-request";
import { useGetUserCoworkersQuery } from "users/information/coworker-information/_api/user-coworker-api";
import UserCoworker from "users/information/coworker-information/user-coworker";

export const PERMISSION_GRANTEE_SEARCH_DEBOUNCE_MS = 300;
export const PERMISSION_GRANTEE_SEARCH_MINIMUM_LENGTH = 2;
export const PERMISSION_GRANTEE_SEARCH_PAGE_SIZE = 10;
export const PERMISSION_GRANTEE_SEARCH_MAXIMUM_LENGTH = USER_COWORKER_QUERY_MAXIMUM_LENGTH;

export type PermissionGranteeOption = {
  individual?: Individual;
  label: string;
  secondaryLabel?: string;
  value: string;
};

export function shouldSearchPermissionGrantees(search: string, enabled = true): boolean {
  const normalizedSearch = search.trim();
  return enabled &&
    normalizedSearch.length >= PERMISSION_GRANTEE_SEARCH_MINIMUM_LENGTH &&
    normalizedSearch.length <= PERMISSION_GRANTEE_SEARCH_MAXIMUM_LENGTH;
}

export function isPermissionGranteeSearchTextTooLong(search: string): boolean {
  return search.trim().length > PERMISSION_GRANTEE_SEARCH_MAXIMUM_LENGTH;
}

export function createPermissionGranteeSearchQuery(search: string): UserCoworkerQuery {
  return new UserCoworkerQuery(
    new UserCoworkerFilterParameters(),
    new UserCoworkerOrderParameters(UserCoworkerOrderField.Name, OrderDirection.Ascending),
    PaginationParameters.create({ pageIndex: 0, pageSize: PERMISSION_GRANTEE_SEARCH_PAGE_SIZE }),
    search.trim(),
  );
}

export function getPermissionGranteeOptionFromCoworker(coworker: UserCoworker): PermissionGranteeOption {
  return {
    individual: coworker.asIndividual(),
    value: coworker.userId.value,
    label: coworker.name.fullName,
    secondaryLabel: coworker.email.toString(),
  };
}

export function mergePermissionGranteeOptions(
  selectedOptions: readonly PermissionGranteeOption[],
  searchOptions: readonly PermissionGranteeOption[],
): PermissionGranteeOption[] {
  const optionsByUserId = new Map<string, PermissionGranteeOption>();

  for (const option of [...selectedOptions, ...searchOptions]) {
    if (optionsByUserId.has(option.value)) continue;
    optionsByUserId.set(option.value, option);
  }

  return [...optionsByUserId.values()];
}

export function usePermissionGranteeTypeahead(searchInput: string, enabled = true) {
  const normalizedSearchInput = searchInput.trim();
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timeoutId = globalThis.setTimeout(() => {
      setDebouncedSearch(normalizedSearchInput);
    }, PERMISSION_GRANTEE_SEARCH_DEBOUNCE_MS);

    return () => globalThis.clearTimeout(timeoutId);
  }, [normalizedSearchInput]);

  const isSearchReady =
    shouldSearchPermissionGrantees(normalizedSearchInput, enabled) && debouncedSearch === normalizedSearchInput;
  const isSearchTextTooLong = isPermissionGranteeSearchTextTooLong(normalizedSearchInput);
  const queryArgs = useMemo(
    () =>
      isSearchReady ? { query: createPermissionGranteeSearchQuery(debouncedSearch), append: false } : skipToken,
    [debouncedSearch, isSearchReady],
  );
  const coworkerQuery = useGetUserCoworkersQuery(queryArgs);

  const options = useMemo(
    () =>
      UserCoworker.fromJSONArray(coworkerQuery.data?.data ?? []).map(getPermissionGranteeOptionFromCoworker),
    [coworkerQuery.data],
  );

  return {
    options,
    isError: coworkerQuery.isError,
    isFetching: coworkerQuery.isFetching,
    isSearchReady,
    isSearchTextTooLong,
  };
}

export default usePermissionGranteeTypeahead;
